import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";
import {useEffect} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, fade, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslate} from "@repo/i18n";

import type {generateTwoFactorRecoveryCodes} from "./__graphql-types__/generateTwoFactorRecoveryCodes.ts";

import warning from "@phosphor-icons/core/bold/warning-bold.svg";
import copy from "@phosphor-icons/core/regular/copy.svg";
import downloadSimple from "@phosphor-icons/core/regular/download-simple.svg";

type Props = {
    /* A function to call when the user has successfully changed their settings.
    Pops up a toast and refreshes the page. */
    onSettingsChanged: () => unknown;
    /* Whether the user is regenerating their recovery codes. */
    regenerating: boolean;
};

export const GENERATE_2FA_RECOVERY_CODES_MUTATION: GqlOperation<generateTwoFactorRecoveryCodes> =
    gqlOp(gql`
        mutation generateTwoFactorRecoveryCodes {
            generateTwoFactorRecoveryCodes {
                recoveryCodes
                error {
                    code
                }
            }
        }
    `);

function displayRecoveryCodes(
    recoveryCodes: (string | null | undefined)[],
): React.ReactElement {
    const downloadCodes = () => {
        if (!recoveryCodes) {
            return;
        }
        const element = document.createElement("a");
        const file = new Blob([recoveryCodes.join("\r\n")], {
            type: "text/plain",
        });
        element.href = URL.createObjectURL(file);
        element.download = "khan-academy-recovery-codes.txt";
        // Required for this to work in FireFox
        document.body?.appendChild(element);
        element.click();
    };

    return (
        <View>
            <View style={styles.grid}>
                {[...Array(10).keys()].map((i) => (
                    <HeadingSmall style={styles.cell} key={i}>
                        {recoveryCodes[i]}
                    </HeadingSmall>
                ))}
            </View>
            <Strut size={spacing.medium_16} />
            <View style={styles.row}>
                <Button
                    kind="secondary"
                    size="small"
                    startIcon={copy}
                    onClick={() => {
                        navigator.clipboard.writeText(
                            recoveryCodes.join("\r\n"),
                        );
                    }}
                >
                    {doNotTranslate`Copy`}
                </Button>
                <Strut size={spacing.xLarge_32} />
                <Button
                    kind="secondary"
                    size="small"
                    startIcon={downloadSimple}
                    onClick={downloadCodes}
                >
                    {doNotTranslate`Download`}
                </Button>
            </View>
        </View>
    );
}

// NOTE: This is a WIP modal, behind a feature flag ("two_factor_authentication")
// and is not enabled for any users.
// This is a modal for showing the user their 2FA recovery codes, called after
// 2FA has been successfully enabled or when a user requests to regenerate their
// 2FA codes.
export function RecoveryCodesTwoFactorModal({
    onSettingsChanged,
    regenerating,
}: Props): React.ReactElement {
    const [generateTwoFactorRecoveryCodes, result] = useMutation(
        GENERATE_2FA_RECOVERY_CODES_MUTATION,
    );

    // On first render, generate the recovery codes to display.
    useEffect(() => {
        generateTwoFactorRecoveryCodes();
    }, [generateTwoFactorRecoveryCodes]);

    let innerContent;
    if (result?.status === "loading" || result?.status === "no-data") {
        innerContent = <CircularSpinner />;
    }
    if (
        result?.status === "success" &&
        result?.data.generateTwoFactorRecoveryCodes?.recoveryCodes &&
        result?.data.generateTwoFactorRecoveryCodes?.recoveryCodes.length === 10
    ) {
        innerContent = displayRecoveryCodes(
            result?.data.generateTwoFactorRecoveryCodes?.recoveryCodes,
        );
    }
    if (
        result?.status === "error" ||
        (result?.status === "success" &&
            result.data.generateTwoFactorRecoveryCodes?.error)
    ) {
        innerContent = (
            <Body style={styles.errorText}>
                {t`Oops! Something went wrong. Please try again.`}
            </Body>
        );
    }

    return (
        <OnePaneDialog
            title={doNotTranslate`Two-factor recovery codes`}
            content={
                <View>
                    {regenerating && (
                        <Body style={styles.warningText}>
                            <PhosphorIcon
                                icon={warning}
                                style={{
                                    backgroundColor: color.gold,
                                }}
                            />
                            {doNotTranslate` Your previous recovery codes have been invalidated.`}
                        </Body>
                    )}
                    <Body>
                        {doNotTranslate`You can use these recovery codes to access your Khan Academy account in case you cannot receive two-factor authentication codes. Each code can only be used once.`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                    {innerContent}
                    <Strut size={spacing.medium_16} />
                    <Body style={styles.warningText}>
                        <PhosphorIcon
                            icon={warning}
                            style={{
                                backgroundColor: color.gold,
                            }}
                        />
                        {doNotTranslate` Keep these codes safe! If you lose access to your 2FA device and don't have the recovery codes, you will lose access to your account.`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                </View>
            }
            footer={
                <Button onClick={onSettingsChanged}>
                    {doNotTranslate`OK, I've saved my recovery codes`}
                </Button>
            }
            style={styles.modal}
        />
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    warningText: {
        // Matching the "warning" background color in Banner
        backgroundColor: fade(color.gold, 0.08),
        padding: spacing.small_12,
    },
    modal: {
        height: "90%",
        maxHeight: 680,
    },
    cell: {
        padding: spacing.xSmall_8,
    },
    row: {
        flexDirection: "row",
        justifyContent: "center",
    },
    grid: {
        display: "grid",
        width: "60%",
        marginLeft: "auto",
        marginRight: "auto",
        gridTemplateColumns: "repeat(2, 1fr)",
    },
});
